import React, { ElementRef, useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Box, createStyles, Grid, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { RvLoader } from '../Loader';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { addUserAIActivity, getAIAccessType, UserAIAccessType } from '../../Dashboard/userAIActivity/UserAIActivityRepositories';
import { DashboardTypeEnum } from '../../home/ChartComponents';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import { LightTooltip } from '../../helpers/commonHelper';
import { client } from '../..';
import gql from 'graphql-tag';
import { AIPrompt } from '../../hooks/useAIPromptQuery';
import { showNotification } from '../../App';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        textField: {
            width: '100%',
            flex: 1
        },
        dialogContent: {
            overflowY: 'unset'
        },
        loaderWrapper: {
            display: 'flex',
            flexFlow: 'column',
            gap: 15,
            justifyContent: 'center',
            alignItems: 'center',
        },
        resize: {
            fontSize: 14,
        },
    }),
  );
  
interface AIChartDialogProps extends Pick<DialogProps, 'open' | 'onClose' | 'fullWidth' | 'maxWidth'> {
    query: string | null;
    data: string | ArrayBuffer | null;
    accessSubType: string | null;
    layoutId?: DashboardTypeEnum;
    aiPrompt?: AIPrompt[];
}

export interface AIChartDialogFormValues {
    query: string | null;
    results: string | null;
    isLoading?: boolean;
}

export const AIChartDialog = (props: AIChartDialogProps) => {

    const classes = useStyles();
    
    const styledRef = useRef<ElementRef<'div'>>(null);
    
    const [queryText, setQueryText] = React.useState<string>(props.query || '');

    const [askMore, setAskMore] = React.useState<string | null>(null);

    const [results, setResults] = React.useState<string | null>(null);

    const [isLoading, setIsLoading] = React.useState<boolean>(props.query && props.query.length > 0 ? true : false);

    const [isCopied, setIsCopied] = React.useState<boolean>(false);

    const userQuery = useCurrentUser();
    
    const logAIActivity = () => {
        addUserAIActivity({
            accessType: getAIAccessType(props.layoutId ===  DashboardTypeEnum.FEE_EARNER ? UserAIAccessType.FeeEarnerDashboard : UserAIAccessType.FirmDashboard),
            accessSubType: props.accessSubType ?? '',
            question: askMore
        });
    };

    useEffect(() => {

        if (props.data) {
            let base64OrUrlImage = props.data! ? props.data! : '';

            if (base64OrUrlImage && props.query) {

                setQueryText(props.query);

                fetchEmailSummaryResponse(base64OrUrlImage, queryText, null, false);
            }
        }

          // tslint:disable-next-line: align
    }, []);
    
    const fetchEmailSummaryResponse = (base64OrUrlImage: string | ArrayBuffer | null, queryText: string, systemPromptOverride: string | null, isQueryTextRun: Boolean) => {
        var enquiryQueryParams: ChartAnalyserResponseQueryParams = {
            base64OrUrlImage: base64OrUrlImage,
            queryText,
            systemPromptOverride: systemPromptOverride ?? ''
        };

        retrieveChartAnalyserResponseData(
            enquiryQueryParams,
            (data) => onDataRetrieved(data, isQueryTextRun, queryText),
            // tslint:disable-next-line
            function (reason: any): void {
                showNotification(null, reason, 'error');
                setIsLoading(false);
            }
        );
    };
    
    const onDataRetrieved = (data: AiData, isQueryTextRun: Boolean, queryText: string) => {   

        logAIActivity();       

        if (data && data.ai.chartAnalyserResponse && data.ai.chartAnalyserResponse.chartAnalysis ) {
            setResults(data.ai.chartAnalyserResponse?.chartAnalysis || null);
        }

        setIsLoading(false);
    };

    const onGenerate = async () => {
        setIsLoading(true);
        fetchEmailSummaryResponse(props.data, queryText, null, true);
    };

    const onAskMoreGenerate = async () => {
        
        if (!askMore) {
            return;
        }

        setIsLoading(true);
        fetchEmailSummaryResponse(props.data, askMore, null, true);
    };

    const removeHtmlMarkdown = (str: string): string => {
        return str.replace(/```html/g, '');
    };

    const stripHtmlTags = (html: string): string => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };
    
    const onEmail = () => {

        if (!results) {
            return;
        }

        const plainTextResults = stripHtmlTags(results);
        const mailToString = 'mailto:?body=' + encodeURIComponent(plainTextResults);    

        window.location.href = mailToString;
    };

    const onCopyClipboard = (event: React.MouseEvent<HTMLButtonElement>) => {

        if (!results) {
            return;
        }

        setIsCopied(true);
        
        const element = styledRef.current;

        if (!element) { return; }

        const range = document.createRange();
        range.selectNodeContents(element);

        const selection = window.getSelection();

        if (!selection) { return; }

        selection.removeAllRanges();
        selection.addRange(range);

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                // tslint:disable-next-line: no-console
                console.log('Copied with formatting!');
            } else {
                // tslint:disable-next-line: no-console
                console.log('Copy failed.');
            }
        } catch (err) {
            // tslint:disable-next-line: no-console
            console.error('Copy error:', err);
        }

        // Cleanup
        selection.removeAllRanges();

        setTimeout(() => {
            setIsCopied(false);
              // tslint:disable-next-line: align
        }, 2000);
    };

    const getTitle = () => {
        switch (props.layoutId) {
            case DashboardTypeEnum.LEAD:
                return 'RedView CRM AI Assistant';
            default:
                return 'RedView Acuity AI Assistant';
        }
    };

    return (
        <Dialog 
            {...props}
        >
            <DialogTitle id="form-dialog-title">
                {getTitle()}
            </DialogTitle>
            <DialogContent
                className={classes.dialogContent}
            >
                <Grid container={true} spacing={3}>
                    {userQuery.data?.appUser.isInternalAdmin && (
                        <Grid item={true} xs={12} sm={12} md={12} lg={12}>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                gridGap="10px"
                            >
                                <TextField
                                    label="Query"
                                    type="text"
                                    value={queryText}
                                    onChange={(e) => {
                                        setQueryText(e.target.value);
                                    }}
                                    required={true}
                                    className={classes.textField}
                                    multiline={true}
                                />

                                <Button 
                                    disabled={isLoading}
                                    variant="contained"
                                    onClick={onGenerate}
                                >
                                    Query
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    <Grid item={true} xs={12} sm={12} md={12} lg={12}>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                gridGap="10px"
                            >
                                <TextField
                                    label="Question"
                                    type="text"
                                    value={askMore}
                                    onChange={(e) => {
                                        setAskMore(e.target.value);
                                    }}
                                    required={true}
                                    className={classes.textField}
                                    multiline={true}
                                />

                                <Button 
                                    disabled={isLoading}
                                    variant="contained"
                                    onClick={onAskMoreGenerate}
                                >
                                    Ask More
                                </Button>
                            </Box>
                        </Grid>
                    {isLoading ? (
                        <Grid item={true} xs={12} sm={12} md={12} lg={12}>
                            <div className={classes.loaderWrapper}>
                                <Typography className={classes.resize}>
                                    Please wait while we process your request
                                </Typography>
                                <RvLoader />
                            </div>
                        </Grid>
                    ) : (
                        <Grid item={true} xs={12} sm={12} md={12} lg={12}>
                            <div 
                                dangerouslySetInnerHTML={{ __html: removeHtmlMarkdown(results || '') }}      
                                ref={styledRef}                               
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                style={{
                    justifyContent: 'space-between',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <Button
                        disabled={isLoading || !results}
                        variant="contained"
                        onClick={onEmail}
                    >
                        Email
                    </Button>
                    <LightTooltip
                        title="Copy to clipboard"
                        placement="top"
                    >
                        <IconButton 
                            component="button"
                            disabled={isLoading || !results || isCopied}
                            color="primary"
                            onClick={onCopyClipboard}
                        >
                            <FilterNoneIcon />
                        </IconButton>
                    </LightTooltip>
                </div>
                <Button 
                    onClick={() => {
                        if (props.onClose) {
                            props.onClose({}, 'backdropClick');
                        }
                    }} 
                    variant="contained"
                >
                    cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export interface ChartAnalyserResponseQueryParams {
    base64OrUrlImage: string | ArrayBuffer | null;
    queryText: string;
    systemPromptOverride: string;
}

export interface AiData {
    loading?: boolean;
    networkStatus?: number;
    ai: AI;
}

export interface AI {
    emailExtractionResponse: EmailExtractionResponse;
    emailContactExtractionResponse: EmailContactExtractionResponse;
    emailSummaryResponse: EmailSummaryResponse;
    chartAnalyserResponse: ChartAnalyserResponse;
}

export interface EmailExtractionResponse {
    id:  number;
    firstName: string;
    lastName: string;
    phone: string;
    mobile: string;
    email: string;
    notes: string;
    addressName: string;
    floor: string;
    number: string;
    street: string;
    street2: string;
    suburb: string;
    state: string;
    postcode: string;
    country: string;
    organisation: string;
    detailedEnquiryNotes: string;
    internalNotes: string;
    sourceNotes: string;
    areaOfLaw: string;
}

export interface EmailContactExtractionResponse {
    id:  number;
    type: string;
    orgCompanyName: string;
    orgEmail: string;
    orgTelephone: string;
    orgWebsite: string;
    orgBackgroundNotes: string;
    individualPositionTitle: string;
    individualGivenName: string;
    individualSurname: string;
    individualEmail: string;
    individualPhone: string;
    individualMobile: string;
    individualBackgroundNotes: string;
    addressName: string;
    floor: string;
    number: string;
    street: string;
    street2: string;
    suburb: string;
    state: string;
    postcode: string;
    country: string;
}

export interface EmailSummaryResponse {
    id:  number;
    summary: string;
}

export interface ChartAnalyserResponse {
    id:  number;
    chartAnalysis: string;
}

export function retrieveChartAnalyserResponseData(
    query: ChartAnalyserResponseQueryParams,
    onSuccess: (data: AiData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void,
): void {
    client
        .query({
            query: ChartAnalyserResponseQuery,
            variables: {
                base64OrUrlImage: query.base64OrUrlImage,
                queryText: query.queryText,
                systemPromptOverride: query.systemPromptOverride
            },
            fetchPolicy: 'network-only',
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

const ChartAnalyserResponseQuery = gql`
    query ChartAnalyserResponse($base64OrUrlImage: String, $queryText: String, $systemPromptOverride: String) {
        ai{
            chartAnalyserResponse(base64OrUrlImage: $base64OrUrlImage, queryText: $queryText, systemPromptOverride: $systemPromptOverride) {
                id,
                chartAnalysis
            }
        }
    }
`;